import { useState, useEffect } from 'react'
import { db, storage } from '../firebase/config'
import { collection, addDoc, getDocs, deleteDoc, doc, query, orderBy, serverTimestamp } from 'firebase/firestore'
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage'
import { Upload, Trash2, Image as ImageIcon, Calendar, FileText, MapPin, Clock, Users } from 'lucide-react'
import { toast } from '../hooks/useToast'

function AdminPage({ currentUser }) {
  const [photos, setPhotos] = useState([])
  const [events, setEvents] = useState([])
  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [activeTab, setActiveTab] = useState('photos') // photos, events
  
  // Formulaire photos
  const [selectedEvent, setSelectedEvent] = useState('')
  const [description, setDescription] = useState('')
  const [selectedFile, setSelectedFile] = useState(null)
  const [previewUrl, setPreviewUrl] = useState(null)

  // Formulaire événements
  const [eventData, setEventData] = useState({
    name: '',
    date: '',
    time: '',
    location: '',
    city: '',
    description: '',
    capacity: '',
    photoURL: ''
  })
  const [creatingEvent, setCreatingEvent] = useState(false)

  // ADMIN EMAIL - CHANGE ICI TON EMAIL
  const ADMIN_EMAIL = 'contact@johnnysjh.fr'

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    setLoading(true)
    try {
      // Charger les photos
      const photosQuery = query(
        collection(db, 'gallery_photos'),
        orderBy('uploadedAt', 'desc')
      )
      const photosSnapshot = await getDocs(photosQuery)
      setPhotos(photosSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })))

      // Charger les événements
      const upcomingSnapshot = await getDocs(collection(db, '100johnny', 'main', 'radio_events'))
      const pastSnapshot = await getDocs(collection(db, '100johnny', 'main', 'old_events'))
      
      const allEvents = [
        ...upcomingSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })),
        ...pastSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
      ]
      
      setEvents(allEvents.sort((a, b) => {
        const dateA = a.date?.toDate ? a.date.toDate() : new Date(a.date || 0)
        const dateB = b.date?.toDate ? b.date.toDate() : new Date(b.date || 0)
        return dateB - dateA
      }))
      
    } catch (error) {
      console.error('Erreur chargement:', error)
      toast.error('❌ Erreur de chargement')
    } finally {
      setLoading(false)
    }
  }

  const handleFileSelect = (e) => {
    const file = e.target.files[0]
    if (!file) return

    if (!file.type.startsWith('image/')) {
      toast.error('❌ Veuillez sélectionner une image')
      return
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('❌ Image trop volumineuse (max 5MB)')
      return
    }

    setSelectedFile(file)
    
    const reader = new FileReader()
    reader.onloadend = () => {
      setPreviewUrl(reader.result)
    }
    reader.readAsDataURL(file)
  }

  const handleUpload = async (e) => {
    e.preventDefault()

    if (!selectedFile) {
      toast.error('❌ Sélectionnez une photo')
      return
    }

    if (!selectedEvent) {
      toast.error('❌ Sélectionnez un événement')
      return
    }

    setUploading(true)

    try {
      const timestamp = Date.now()
      const fileName = `gallery/${selectedEvent}_${timestamp}_${selectedFile.name}`
      const storageRef = ref(storage, fileName)
      
      await uploadBytes(storageRef, selectedFile)
      const photoURL = await getDownloadURL(storageRef)

      const event = events.find(e => e.id === selectedEvent)
      const eventName = event?.name || 'Événement'

      await addDoc(collection(db, 'gallery_photos'), {
        eventId: selectedEvent,
        eventName: eventName,
        photoURL: photoURL,
        storagePath: fileName,
        description: description.trim() || '',
        uploadedBy: currentUser?.email || 'admin',
        uploadedAt: serverTimestamp()
      })

      toast.success('✅ Photo ajoutée avec succès !')
      
      setSelectedFile(null)
      setPreviewUrl(null)
      setDescription('')
      setSelectedEvent('')
      document.getElementById('fileInput').value = ''
      
      loadData()
      
    } catch (error) {
      console.error('Erreur upload:', error)
      toast.error('❌ Erreur lors de l\'upload')
    } finally {
      setUploading(false)
    }
  }

  const handleDeletePhoto = async (photo) => {
    if (!window.confirm('Supprimer cette photo ?')) return

    try {
      if (photo.storagePath) {
        const storageRef = ref(storage, photo.storagePath)
        await deleteObject(storageRef)
      }

      await deleteDoc(doc(db, 'gallery_photos', photo.id))
      
      toast.success('✅ Photo supprimée')
      loadData()
      
    } catch (error) {
      console.error('Erreur suppression:', error)
      toast.error('❌ Erreur de suppression')
    }
  }

  // ============================================
  // CRÉATION D'ÉVÉNEMENT + NOTIFICATION
  // ============================================

  const handleCreateEvent = async (e) => {
    e.preventDefault()

    if (!eventData.name || !eventData.date) {
      toast.error('❌ Nom et date requis')
      return
    }

    setCreatingEvent(true)

    try {
      // Créer l'événement dans Firebase
      const eventRef = await addDoc(collection(db, '100johnny', 'main', 'radio_events'), {
        name: eventData.name,
        date: serverTimestamp(),
        time: eventData.time || '',
        location: eventData.location || '',
        city: eventData.city || '',
        description: eventData.description || '',
        capacity: eventData.capacity ? parseInt(eventData.capacity) : null,
        photoURL: eventData.photoURL || '',
        status: 'upcoming',
        createdAt: serverTimestamp(),
        createdBy: currentUser?.email || 'admin'
      })

      toast.success('✅ Événement créé !')
      
      // Reset formulaire
      setEventData({
        name: '',
        date: '',
        time: '',
        location: '',
        city: '',
        description: '',
        capacity: '',
        photoURL: ''
      })
      
      loadData()
      
    } catch (error) {
      console.error('Erreur création événement:', error)
      toast.error('❌ Erreur lors de la création')
    } finally {
      setCreatingEvent(false)
    }
  }

  // Vérifier si l'utilisateur est admin
  if (!currentUser || currentUser.email !== ADMIN_EMAIL) {
    return (
      <div className="page active">
        <div className="container">
          <div className="empty-state">
            <div className="empty-icon">🔒</div>
            <h2 style={{ fontSize: '2rem', marginBottom: '10px' }}>Accès Refusé</h2>
            <p>Cette page est réservée aux administrateurs</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="page active">
      <div className="container">
        <div className="page-header">
          <div className="page-badge">⚙️ ADMIN</div>
          <h1 className="page-title">Administration</h1>
          <p className="page-subtitle">Gestion des contenus et événements</p>
        </div>

        {/* Onglets */}
        <div style={{ display: 'flex', gap: '10px', marginBottom: '30px' }}>
          <button
            onClick={() => setActiveTab('photos')}
            className="btn"
            style={{
              padding: '15px 30px',
              background: activeTab === 'photos' ? 'linear-gradient(135deg, #00d9ff, #0ea5e9)' : 'rgba(255,255,255,0.05)',
              border: activeTab === 'photos' ? 'none' : '1px solid rgba(255,255,255,0.2)',
              color: '#fff',
              fontWeight: 700
            }}
          >
            📷 Galerie Photos ({photos.length})
          </button>
          <button
            onClick={() => setActiveTab('events')}
            className="btn"
            style={{
              padding: '15px 30px',
              background: activeTab === 'events' ? 'linear-gradient(135deg, #ffd700, #f59e0b)' : 'rgba(255,255,255,0.05)',
              border: activeTab === 'events' ? 'none' : '1px solid rgba(255,255,255,0.2)',
              color: '#fff',
              fontWeight: 700
            }}
          >
            📅 Événements ({events.length})
          </button>
        </div>

        {/* TAB: PHOTOS */}
        {activeTab === 'photos' && (
          <>
            {/* Formulaire d'upload photo */}
            <div className="glass-card" style={{ marginBottom: '30px' }}>
              <h2 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Upload size={24} color="#00d9ff" />
                Ajouter une photo
              </h2>

              <form onSubmit={handleUpload}>
                <div style={{ display: 'grid', gap: '20px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                      <Calendar size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                      Événement *
                    </label>
                    <select
                      value={selectedEvent}
                      onChange={(e) => setSelectedEvent(e.target.value)}
                      required
                      style={{
                        width: '100%',
                        padding: '12px 15px',
                        background: 'rgba(255,255,255,0.05)',
                        border: '1px solid rgba(255,255,255,0.2)',
                        borderRadius: '12px',
                        color: '#fff',
                        fontSize: '1rem',
                        outline: 'none'
                      }}
                    >
                      <option value="">-- Sélectionner un événement --</option>
                      {events.map((event) => (
                        <option key={event.id} value={event.id}>
                          {event.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                      <ImageIcon size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                      Photo *
                    </label>
                    <input
                      id="fileInput"
                      type="file"
                      accept="image/*"
                      onChange={handleFileSelect}
                      required
                      style={{
                        width: '100%',
                        padding: '12px 15px',
                        background: 'rgba(255,255,255,0.05)',
                        border: '1px solid rgba(255,255,255,0.2)',
                        borderRadius: '12px',
                        color: '#fff',
                        fontSize: '1rem'
                      }}
                    />
                  </div>

                  {previewUrl && (
                    <div>
                      <img 
                        src={previewUrl} 
                        alt="Preview"
                        style={{
                          width: '100%',
                          maxHeight: '300px',
                          objectFit: 'contain',
                          borderRadius: '12px',
                          border: '1px solid rgba(255,255,255,0.2)'
                        }}
                      />
                    </div>
                  )}

                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                      <FileText size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                      Description (optionnel)
                    </label>
                    <textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Légende de la photo..."
                      rows={3}
                      style={{
                        width: '100%',
                        padding: '12px 15px',
                        background: 'rgba(255,255,255,0.05)',
                        border: '1px solid rgba(255,255,255,0.2)',
                        borderRadius: '12px',
                        color: '#fff',
                        fontSize: '1rem',
                        outline: 'none',
                        resize: 'vertical'
                      }}
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={uploading}
                    className="btn btn-primary"
                    style={{
                      padding: '15px',
                      fontSize: '1.1rem',
                      fontWeight: 700,
                      opacity: uploading ? 0.6 : 1
                    }}
                  >
                    {uploading ? '⏳ Upload en cours...' : '✅ Ajouter la photo'}
                  </button>
                </div>
              </form>
            </div>

            {/* Liste des photos */}
            <div className="glass-card">
              <h2 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '20px' }}>
                📷 Photos publiées ({photos.length})
              </h2>

              {loading ? (
                <div className="loading-spinner">
                  <div className="spinner"></div>
                  <p>Chargement...</p>
                </div>
              ) : photos.length === 0 ? (
                <p style={{ textAlign: 'center', color: 'rgba(255,255,255,0.5)', padding: '40px' }}>
                  Aucune photo dans la galerie
                </p>
              ) : (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                  gap: '15px'
                }}>
                  {photos.map((photo) => (
                    <div
                      key={photo.id}
                      style={{
                        position: 'relative',
                        borderRadius: '12px',
                        overflow: 'hidden',
                        border: '1px solid rgba(255,255,255,0.1)',
                        background: 'rgba(255,255,255,0.03)'
                      }}
                    >
                      <img
                        src={photo.photoURL}
                        alt={photo.description}
                        style={{
                          width: '100%',
                          height: '200px',
                          objectFit: 'cover'
                        }}
                      />
                      
                      <div style={{ padding: '10px' }}>
                        <div style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: '5px', color: '#00d9ff' }}>
                          {photo.eventName}
                        </div>
                        {photo.description && (
                          <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.6)', marginBottom: '10px' }}>
                            {photo.description}
                          </div>
                        )}
                        
                        <button
                          onClick={() => handleDeletePhoto(photo)}
                          className="btn"
                          style={{
                            width: '100%',
                            padding: '8px',
                            background: 'rgba(239, 68, 68, 0.2)',
                            border: '1px solid rgba(239, 68, 68, 0.4)',
                            color: '#ef4444',
                            fontSize: '0.85rem',
                            fontWeight: 600,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '6px'
                          }}
                        >
                          <Trash2 size={14} />
                          Supprimer
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {/* TAB: ÉVÉNEMENTS */}
        {activeTab === 'events' && (
          <>
            {/* Formulaire création événement */}
            <div className="glass-card" style={{ marginBottom: '30px' }}>
              <h2 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Calendar size={24} color="#ffd700" />
                Créer un nouvel événement
              </h2>

              <form onSubmit={handleCreateEvent}>
                <div style={{ display: 'grid', gap: '20px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                      Titre de l'événement *
                    </label>
                    <input
                      type="text"
                      value={eventData.name}
                      onChange={(e) => setEventData({...eventData, name: e.target.value})}
                      placeholder="Ex: Concert hommage à Johnny"
                      required
                      style={{
                        width: '100%',
                        padding: '12px 15px',
                        background: 'rgba(255,255,255,0.05)',
                        border: '1px solid rgba(255,255,255,0.2)',
                        borderRadius: '12px',
                        color: '#fff',
                        fontSize: '1rem',
                        outline: 'none'
                      }}
                    />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                        <Calendar size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                        Date *
                      </label>
                      <input
                        type="date"
                        value={eventData.date}
                        onChange={(e) => setEventData({...eventData, date: e.target.value})}
                        required
                        style={{
                          width: '100%',
                          padding: '12px 15px',
                          background: 'rgba(255,255,255,0.05)',
                          border: '1px solid rgba(255,255,255,0.2)',
                          borderRadius: '12px',
                          color: '#fff',
                          fontSize: '1rem',
                          outline: 'none'
                        }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                        <Clock size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                        Heure
                      </label>
                      <input
                        type="time"
                        value={eventData.time}
                        onChange={(e) => setEventData({...eventData, time: e.target.value})}
                        style={{
                          width: '100%',
                          padding: '12px 15px',
                          background: 'rgba(255,255,255,0.05)',
                          border: '1px solid rgba(255,255,255,0.2)',
                          borderRadius: '12px',
                          color: '#fff',
                          fontSize: '1rem',
                          outline: 'none'
                        }}
                      />
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                        <MapPin size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                        Lieu
                      </label>
                      <input
                        type="text"
                        value={eventData.location}
                        onChange={(e) => setEventData({...eventData, location: e.target.value})}
                        placeholder="Ex: Stade de France"
                        style={{
                          width: '100%',
                          padding: '12px 15px',
                          background: 'rgba(255,255,255,0.05)',
                          border: '1px solid rgba(255,255,255,0.2)',
                          borderRadius: '12px',
                          color: '#fff',
                          fontSize: '1rem',
                          outline: 'none'
                        }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                        Ville
                      </label>
                      <input
                        type="text"
                        value={eventData.city}
                        onChange={(e) => setEventData({...eventData, city: e.target.value})}
                        placeholder="Ex: Paris"
                        style={{
                          width: '100%',
                          padding: '12px 15px',
                          background: 'rgba(255,255,255,0.05)',
                          border: '1px solid rgba(255,255,255,0.2)',
                          borderRadius: '12px',
                          color: '#fff',
                          fontSize: '1rem',
                          outline: 'none'
                        }}
                      />
                    </div>
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                      <Users size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                      Capacité (places)
                    </label>
                    <input
                      type="number"
                      value={eventData.capacity}
                      onChange={(e) => setEventData({...eventData, capacity: e.target.value})}
                      placeholder="Ex: 500"
                      style={{
                        width: '100%',
                        padding: '12px 15px',
                        background: 'rgba(255,255,255,0.05)',
                        border: '1px solid rgba(255,255,255,0.2)',
                        borderRadius: '12px',
                        color: '#fff',
                        fontSize: '1rem',
                        outline: 'none'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                      <FileText size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                      Description
                    </label>
                    <textarea
                      value={eventData.description}
                      onChange={(e) => setEventData({...eventData, description: e.target.value})}
                      placeholder="Décrivez l'événement..."
                      rows={5}
                      style={{
                        width: '100%',
                        padding: '12px 15px',
                        background: 'rgba(255,255,255,0.05)',
                        border: '1px solid rgba(255,255,255,0.2)',
                        borderRadius: '12px',
                        color: '#fff',
                        fontSize: '1rem',
                        outline: 'none',
                        resize: 'vertical'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                      <ImageIcon size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                      URL de l'image
                    </label>
                    <input
                      type="url"
                      value={eventData.photoURL}
                      onChange={(e) => setEventData({...eventData, photoURL: e.target.value})}
                      placeholder="https://example.com/image.jpg"
                      style={{
                        width: '100%',
                        padding: '12px 15px',
                        background: 'rgba(255,255,255,0.05)',
                        border: '1px solid rgba(255,255,255,0.2)',
                        borderRadius: '12px',
                        color: '#fff',
                        fontSize: '1rem',
                        outline: 'none'
                      }}
                    />
                    {eventData.photoURL && (
                      <img 
                        src={eventData.photoURL} 
                        alt="Aperçu" 
                        style={{ 
                          width: '100%', 
                          maxHeight: '200px', 
                          objectFit: 'cover', 
                          borderRadius: '12px',
                          marginTop: '10px',
                          border: '1px solid rgba(255,255,255,0.2)'
                        }}
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    )}
                  </div>

                  {/* Aperçu notification */}
                  {eventData.name && eventData.date && (
                    <div style={{ 
                      padding: '15px', 
                      background: 'rgba(0, 217, 255, 0.1)', 
                      border: '1px solid rgba(0, 217, 255, 0.3)', 
                      borderRadius: '12px' 
                    }}>
                      <div style={{ fontSize: '0.9rem', fontWeight: 700, marginBottom: '8px', color: '#00d9ff' }}>
                        📱 Aperçu de la notification
                      </div>
                      <div style={{ 
                        padding: '12px', 
                        background: 'rgba(0,0,0,0.3)', 
                        borderRadius: '8px',
                        fontSize: '0.9rem'
                      }}>
                        <div style={{ fontWeight: 700, marginBottom: '4px' }}>📅 Nouvel événement</div>
                        <div>{eventData.name}</div>
                        <div style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.6)', marginTop: '4px' }}>
                          {new Date(eventData.date).toLocaleDateString('fr-FR')}
                          {eventData.time && ` à ${eventData.time}`}
                        </div>
                      </div>
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                      type="submit"
                      disabled={creatingEvent}
                      className="btn btn-primary"
                      style={{
                        flex: 1,
                        padding: '15px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        opacity: creatingEvent ? 0.7 : 1
                      }}
                    >
                      {creatingEvent ? (
                        <>⏳ Création en cours...</>
                      ) : (
                        <>
                          <Calendar size={20} />
                          Créer l'événement
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </form>
            </div>

            {/* Liste des événements */}
            <div className="glass-card">
              <h2 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '20px' }}>
                📅 Événements existants ({events.length})
              </h2>

              {loading ? (
                <div className="loading-spinner">
                  <div className="spinner"></div>
                  <p>Chargement...</p>
                </div>
              ) : events.length === 0 ? (
                <p style={{ textAlign: 'center', color: 'rgba(255,255,255,0.5)', padding: '40px' }}>
                  Aucun événement créé
                </p>
              ) : (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                  gap: '20px'
                }}>
                  {events.map((event) => (
                    <div
                      key={event.id}
                      style={{
                        padding: '20px',
                        background: 'rgba(255,255,255,0.03)',
                        borderRadius: '15px',
                        border: '1px solid rgba(255,255,255,0.1)'
                      }}
                    >
                      {event.photoURL && (
                        <img 
                          src={event.photoURL}
                          alt={event.name}
                          style={{
                            width: '100%',
                            height: '150px',
                            objectFit: 'cover',
                            borderRadius: '10px',
                            marginBottom: '15px'
                          }}
                        />
                      )}
                      <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '10px' }}>
                        {event.name}
                      </h3>
                      <div style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.7)', marginBottom: '5px' }}>
                        📅 {event.date?.toDate ? event.date.toDate().toLocaleDateString('fr-FR') : 'Date non définie'}
                      </div>
                      {event.location && (
                        <div style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.7)' }}>
                          📍 {event.location}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        <div className="dock-spacer"></div>
      </div>
    </div>
  )
}

export default AdminPage